import { supabase } from './supabase';

export interface DailyStats {
  date: string;
  active_users: number;
  new_users: number;
  total_votes: number;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

class ReportsAdminService {
  /**
   * Get stats for a date range
   */
  async getStats(range: DateRange): Promise<DailyStats[]> {
    const { startDate, endDate } = range;
    return this.calculateStatsManually(startDate, endDate);
  }

  /**
   * Calculate stats manually (optimized - single queries)
   */
  private async calculateStatsManually(startDate: string, endDate: string): Promise<DailyStats[]> {
    // Crear fechas en UTC para evitar problemas de timezone
    const start = new Date(startDate + 'T00:00:00.000Z');
    const end = new Date(endDate + 'T23:59:59.999Z');

    // Single query for all votes in range
    const { data: votes } = await supabase
      .from('votes')
      .select('user_id, created_at')
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString());

    // Single query for new users in range
    const { data: newUsers } = await supabase
      .from('profiles')
      .select('created_at')
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString());

    // Group data by date
    const statsByDate = new Map<string, DailyStats>();

    // Initialize all dates in range
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      statsByDate.set(dateStr, {
        date: dateStr,
        active_users: 0,
        new_users: 0,
        total_votes: 0,
      });
    }

    // Process votes
    const activeUsersByDate = new Map<string, Set<string>>();
    (votes || []).forEach((vote: any) => {
      const date = vote.created_at.split('T')[0];
      const stats = statsByDate.get(date);
      if (stats) {
        stats.total_votes++;
        if (!activeUsersByDate.has(date)) {
          activeUsersByDate.set(date, new Set());
        }
        activeUsersByDate.get(date)!.add(vote.user_id);
      }
    });

    // Process new users
    (newUsers || []).forEach((user: any) => {
      const date = user.created_at.split('T')[0];
      const stats = statsByDate.get(date);
      if (stats) {
        stats.new_users++;
      }
    });

    // Set active users count
    activeUsersByDate.forEach((userIds, date) => {
      const stats = statsByDate.get(date);
      if (stats) {
        stats.active_users = userIds.size;
      }
    });

    // Convert to array and sort by date (newest first)
    return Array.from(statsByDate.values()).sort((a, b) => b.date.localeCompare(a.date));
  }

  /**
   * Get quick date ranges (usando UTC para consistencia)
   */
  getDateRange(period: string): DateRange {
    const now = new Date();
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const start = new Date(end);

    switch (period) {
      case 'today':
        // start ya está en el inicio de hoy (UTC)
        break;
      case 'yesterday':
        start.setUTCDate(start.getUTCDate() - 1);
        end.setUTCDate(end.getUTCDate() - 1);
        break;
      case 'week':
        start.setUTCDate(start.getUTCDate() - 7);
        break;
      case 'month':
        start.setUTCMonth(start.getUTCMonth() - 1);
        break;
      case '3months':
        start.setUTCMonth(start.getUTCMonth() - 3);
        break;
      case '6months':
        start.setUTCMonth(start.getUTCMonth() - 6);
        break;
      default:
        start.setUTCDate(start.getUTCDate() - 7);
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }
}

export const reportsAdminService = new ReportsAdminService();
